import React, {useEffect, useRef, useState} from "react";
import {
    Accordion,
    AccordionDetails,
    AccordionSummary,
    Box,
    Card,
    Dialog,
    DialogContent,
    IconButton,
    Typography
} from "@mui/material";
import FullscreenIcon from '@mui/icons-material/Fullscreen';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import CloseIcon from '@mui/icons-material/Close';
import axios from "axios";
import {enqueueSnackbar} from "notistack";

import {formatDuration, formatUTCDate} from "../../../utils/utils";
import {deletePublication, getSocialOptions} from "../../../queries/socialQueries";
import {generateClipLogicDescriptions, getLogicClipPublications} from "@/queries/clipQueries";
import {findClosestAspectRatio} from "../../../utils/generic";
import {
    ASPECT_RATIO_OPTIONS
} from "../../../reactvideoeditor/pro/components/advanced-timeline/components/timeline-header/aspect-ratio-dropdown";

import {PlatformPublishSection} from "./PlatformPublishSection";
import {PublicationHistory} from "./PublicationHistory";
import {
    EnrichedSocialContentPublication,
    SocialContentPublication,
    SocialPlatformNames,
    SocialPostNames
} from "../../../types/socialTypes";

import {defaultLanguages, defaultStyles, useSocialPublication} from "../../../contexts/SocialPublicationContext";
import {CollectionClip, LogicClipDescription} from "../../../types/collectionTypes";

export enum AGGREGATION_USAGE_TYPE {
    STANDARD = 'standard',
    PRESET = 'preset',
}

const supportedPostAspectRatio: any = {
    [SocialPlatformNames.facebook]: { [SocialPostNames.reel]: ['9:16'], [SocialPostNames.story]: ['9:16'] }
};

interface SocialPublishProps {
    logicClip: CollectionClip | null;
    onLogicClipDescriptionChange: (logicClipDescription: LogicClipDescription[]) => void;
    usageType?: AGGREGATION_USAGE_TYPE;
    onPresetPublish?: (payload: any) => Promise<void> | void;
}

const SocialPublish: React.FC<SocialPublishProps> = ({
                                                         logicClip,
                                                         usageType = AGGREGATION_USAGE_TYPE.STANDARD,
                                                         onPresetPublish,
                                                         onLogicClipDescriptionChange
                                                     }) => {
    const isStandard = usageType === AGGREGATION_USAGE_TYPE.STANDARD;

    const [publications, setPublications] = useState<SocialContentPublication[]>([]);
    const [enrichedSocialPublications, setEnrichedSocialPublications] = useState<EnrichedSocialContentPublication[]>([]);
    const [draftPublications, setDraftPublications] = useState<Record<string, SocialContentPublication>>({});

    const [previewOpen, setPreviewOpen] = useState(false);

    const abortControllerGetPosts = useRef<AbortController | null>(null);
    const postPollingInterval = useRef<any>(null);
    const deletingPublication = useRef<boolean>(false);
    const videoRef = useRef<HTMLVideoElement>(null);

    const { resetContext, socialOptions, loadSocialOptions } = useSocialPublication();

    const clipAR = logicClip?.materializations?.[0]?.aspect_ratio
        ? findClosestAspectRatio(logicClip.materializations[0].aspect_ratio, ASPECT_RATIO_OPTIONS.map(r => r.label))
        : '';

    const inProgressPublication = (list: SocialContentPublication[]) => list.some(pub => pub.status === 'in_progress' || pub.status === 'processing');

    const fetchPublications = async () => {
        if (!logicClip) return;
        try {
            if (!deletingPublication.current) {
                if (abortControllerGetPosts.current) abortControllerGetPosts.current.abort();
                abortControllerGetPosts.current = new AbortController();
                const tempPublications = await getLogicClipPublications(logicClip.id, abortControllerGetPosts.current);
                setPublications(tempPublications);

                if (!inProgressPublication(tempPublications)) {
                    stopPolling();
                }
            }
        } catch (error) {
            if (!axios.isCancel(error)) console.error("Error polling", error);
        }
    };

    const startPolling = () => {
        if (!postPollingInterval.current) {
            postPollingInterval.current = setInterval(fetchPublications, 5000);
        }
    };

    const stopPolling = () => {
        if (postPollingInterval.current) {
            clearInterval(postPollingInterval.current);
            postPollingInterval.current = null;
        }
    };

    const onPublicationAdd = (socialPost: SocialContentPublication) => {
        if (isStandard) {
            setPublications(prev => {
                socialPost.materialization = logicClip?.materializations[0]
                const newList = [...prev, socialPost];
                if (socialPost.status === 'in_progress') startPolling();
                return newList;
            });
        }
    };

    const handlePublicationDelete = async (publicationId: string) => {
        try {
            deletingPublication.current = true;
            setPublications(prev => {
                const newList = prev.filter(p => p.content_publication_id !== publicationId);
                if (!inProgressPublication(newList)) stopPolling();
                return newList;
            });
            await deletePublication(publicationId);
            enqueueSnackbar('Publication canceled successfully', { variant: 'success' });
        } catch (e) {
            enqueueSnackbar('Failed to cancel the publication.', { variant: 'error' });
        } finally {
            deletingPublication.current = false;
        }
    };

    const enrichPublication = (pub: SocialContentPublication, accountsDict: any, platformsDict: any, typesDict: any): EnrichedSocialContentPublication => {
        const account = accountsDict[pub.social_account_id];
        return {
            ...pub,
            social_account_name: account?.value || "",
            social_platform_name: platformsDict[account?.socialPlatform]?.value || "",
            social_content_name: typesDict[pub.social_content_id]?.value || "",
            published_at: formatUTCDate(pub.published_at, false),
            aspect_ratio: findClosestAspectRatio(pub.materialization.aspect_ratio, ASPECT_RATIO_OPTIONS.map(a => a.label)),
            view: pub.insights ? pub.insights.viewCount : "" ,
            like: pub.insights ? pub.insights.likeCount : "",
            comment: pub.insights ? pub.insights.commentCount : "",
            share: pub.insights ? pub.insights.shareCount : "",
            save: pub.insights ? pub.insights.saveCount : "",
        }
    }

    useEffect(() => {
        if (socialOptions && publications.length >= 0) {
            const accountsDict: any = {};
            socialOptions.socialAccounts.forEach((a: any) => accountsDict[a.id] = a);
            const platformsDict: any = {};
            socialOptions.socialPlatforms.forEach((p: any) => platformsDict[p.id] = p);
            const typesDict: any = {};
            socialOptions.socialPostTypes.forEach((t: any) => typesDict[t.id] = t);
            const tempDraftPublications: Record<string, SocialContentPublication> = {}

            const realPublications = publications.filter(pb => !pb.draft)
            const draftPublications = publications.filter(pb => pb.draft)

            const enrichedRealPublication = realPublications.map(pub => enrichPublication(pub, accountsDict, platformsDict, typesDict));
            draftPublications.forEach(pub => {
                tempDraftPublications[pub.social_account_id] = pub;
            });

            setEnrichedSocialPublications(enrichedRealPublication);
            setDraftPublications(tempDraftPublications)
        }
    }, [publications, socialOptions]);

    useEffect(() => {
        const load = async () => {
            try {
                // Usiamo la funzione del contesto
                if (!socialOptions) {
                    await loadSocialOptions();
                }

                if (isStandard && logicClip?.id) {
                    const pubs = await getLogicClipPublications(logicClip.id);
                    setPublications(pubs);
                    if (inProgressPublication(pubs)) startPolling();
                }
            } catch (e) {
                enqueueSnackbar('Unable to load social options', { variant: 'error' })
            }
        };
        load();
        return () => stopPolling();
    }, [logicClip?.id, isStandard, socialOptions, loadSocialOptions]);

    useEffect(() => {
        return () => {
            if(usageType === AGGREGATION_USAGE_TYPE.STANDARD) {
                resetContext();
            }
        };
    }, [resetContext]);

    useEffect(() => {
        const fetchInitialDescriptions = async () => {
            if (logicClip?.id && (!logicClip.generated_descriptions || logicClip.generated_descriptions.length === 0)) {
                try {
                    const newDescriptions = await generateClipLogicDescriptions(
                        logicClip.id,
                        defaultLanguages.map(l => l.key),
                        defaultStyles.map(s => s.key).concat("title")
                    );
                    onLogicClipDescriptionChange(newDescriptions);
                } catch (error) {
                    console.error("Errori during initial descriptions generation", error);
                }
            }
        };

        fetchInitialDescriptions();
    }, [logicClip?.id]);

    return (
        <Card sx={{ px: 3, py: 2, borderRadius: 0 }}>

            {isStandard && logicClip && (
                <Box sx={{ p: 2, bgcolor: 'action.hover', borderLeft: '5px solid', borderColor: 'primary.main', display: 'flex', justifyContent: 'space-between' }}>
                    <Box>
                        <Typography variant="h5" fontWeight="bold">{logicClip?.title}</Typography>
                        <Box display="flex" gap={4} mt={1}>
                            <Box><Typography variant="caption" fontWeight="bold" display="block" color="text.secondary">ASPECT RATIO</Typography><Typography variant="body2">{clipAR}</Typography></Box>
                            <Box><Typography variant="caption" fontWeight="bold" display="block" color="text.secondary">DURATION</Typography><Typography variant="body2">{formatDuration(logicClip?.duration)}</Typography></Box>
                        </Box>
                    </Box>
                    <Box sx={{ position: 'relative', width: 140, height: 80, bgcolor: 'black', borderRadius: 1, overflow: 'hidden', "&:hover .fs-btn": { opacity: 1 } }}>
                        <video ref={videoRef} muted loop playsInline style={{ width: '100%', height: '100%', objectFit: 'contain' }} onMouseEnter={() => videoRef.current?.play()} onMouseLeave={() => videoRef.current?.pause()}>
                            <source src={logicClip?.materializations?.[0]?.preview_url} />
                        </video>
                        <IconButton className="fs-btn" size="small" onClick={() => setPreviewOpen(true)} sx={{ position: 'absolute', bottom: 2, right: 2, color: 'white', opacity: 0, transition: '0.2s', bgcolor: 'rgba(0,0,0,0.4)' }}><FullscreenIcon fontSize="small" /></IconButton>
                    </Box>
                </Box>
            )}

            {socialOptions && socialOptions.socialPlatforms.map((platform: any) => (
                <PlatformPublishSection
                    key={platform.id}
                    platform={platform}
                    logicClip={logicClip}
                    usageType={usageType}
                    onPresetPublish={onPresetPublish}
                    aspectRatioLabel={clipAR}
                    supportedPostAspectRatio={supportedPostAspectRatio}
                    onPublicationAdd={onPublicationAdd}
                    availableAccounts={socialOptions.socialAccounts.filter((a: any) => a.socialPlatform === platform.id)}
                    availablePostTypes={socialOptions.socialPostTypes.filter((pt: any) => pt.socialPlatforms.includes(platform.id))}
                    draftPublications={draftPublications}
                    onLogicClipDescriptionChange={onLogicClipDescriptionChange}
                />
            ))}

            {isStandard && (
                <Accordion sx={{ mt: 2 }} defaultExpanded={false}>
                    <AccordionSummary expandIcon={<ExpandMoreIcon />}><Typography fontWeight="bold">Clip Publication History ({enrichedSocialPublications.length})</Typography></AccordionSummary>
                    <AccordionDetails>
                        {enrichedSocialPublications.length > 0 && <PublicationHistory rows={enrichedSocialPublications} onPublicationDelete={handlePublicationDelete} />}
                    </AccordionDetails>
                </Accordion>
            )}

            {isStandard && logicClip && (
                <Dialog open={previewOpen} onClose={() => setPreviewOpen(false)} maxWidth="lg" fullWidth>
                    <IconButton onClick={() => setPreviewOpen(false)} sx={{ position: 'absolute', right: 8, top: 8, color: 'white', zIndex: 10 }}><CloseIcon /></IconButton>
                    <DialogContent sx={{ p: 0, bgcolor: 'black', height: '80vh', display: 'flex' }}>
                        <video controls autoPlay style={{ width: '100%', height: '100%' }}><source src={logicClip?.materializations?.[0]?.preview_url} /></video>
                    </DialogContent>
                </Dialog>
            )}
        </Card>
    );
};

export default SocialPublish;